/**
 * SAT Generator 3.0 — useTest Hook (Phase 9: modes + MST sessions)
 * Phase RS: добавлена поддержка MathDomain + MathDifficulty
 * PR-D4-021: Dynamic explanation letter sync after Fisher-Yates shuffle
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { api, ApiError } from '../lib/api-client';
import {
    mapUiDifficultyToVerbal,
    parseVerbalSkillKey,
} from '../lib/verbal-api';
import {
    answersMapFromActiveSession,
    buildCompletedSessionPayload,
    optionLetterFromIndex,
    questionDomain,
} from '../lib/progress';
import type {
    Question,
    TestResult,
    TestMode,
    DifficultyTier,
    MathDomain,
    MathDifficulty,
    ProgressModuleType,
    ProgressSessionMode,
    ActiveProgressSession,
} from '../lib/types';

/** PR-D4-021: Option letter labels for index→letter mapping */
const OPTION_LETTERS = ['A', 'B', 'C', 'D'] as const;

/**
 * PR-D4-021: Sanitize the conclusion sentence in explanation text
 * to reflect the new correct option letter after shuffling.
 *
 * PR-D4-025: Extended to support Russian concluding patterns:
 *   - "Следовательно, правильный ответ — A."
 *   - "Таким образом, правильный вариант B"
 *   - "Поэтому верный ответ В"
 *   - "Значит правильный вариант Г"
 *
 * Does NOT affect LaTeX math variables like $\triangle ABC$ or points $A, B$.
 */
export function sanitizeExplanationLetter(text: string, newLetter: string): string {
    // Combined regex with capture groups for language auto-detection.
    // Group 1: EN full-sentence, Group 2: EN inverted, Group 3: RU patterns.
    const combinedPattern = new RegExp(
        // Group 1: EN full-sentence (Accordingly/Therefore/Thus/Hence/So, the correct choice/answer/option is X)
        '((?:(?:Accordingly|Therefore|Thus|Hence|So),?\\s+)?' +
        '(?:The\\s+)?correct\\s+(?:choice|answer|option)\\s+is\\s+[A-D]\\.?)' +
        '|' +
        // Group 2: EN inverted (choice/option X is correct)
        '((?:choice|option)\\s+[A-D]\\s+is\\s+correct\\.?)' +
        '|' +
        // Group 3: RU (Следовательно/Таким образом/Поэтому/Значит, правильный ответ/вариант/верный ответ X)
        '((?:(?:Следовательно|Таким\\s+образом|Поэтому|Значит),?\\s+)?' +
        '(?:правильный\\s+(?:ответ|вариант)|верный\\s+ответ)\\s*[-—:]?' +
        '\\s*[A-DА-Г]\\.?)',
        'gi',
    );

    return text.replace(combinedPattern, function (_match, _enFull, _enInverted, ru) {
        if (ru !== undefined) {
            return `Следовательно, правильный вариант — ${newLetter}.`;
        }
        return `Therefore, the correct choice is ${newLetter}.`;
    });
}

/**
 * PR-D4-021: Update the explanation field (any supported format)
 * to reflect the new correct option letter.
 */
function updateExplanationLetter(
    explanation: unknown,
    newLetter: string,
): unknown {
    if (typeof explanation === 'string') {
        return sanitizeExplanationLetter(explanation, newLetter);
    }
    if (typeof explanation === 'object' && explanation !== null) {
        const obj = explanation as Record<string, unknown>;

        // LocalizedText: { translations: { en: "...", ru: "..." }, default_language: "en" }
        if (typeof obj.translations === 'object' && obj.translations !== null) {
            const updatedTranslations: Record<string, string> = {};
            for (const [lang, text] of Object.entries(obj.translations)) {
                updatedTranslations[lang] = sanitizeExplanationLetter(String(text), newLetter);
            }
            return { ...obj, translations: updatedTranslations };
        }

        // Record<string, string>: { en: "...", ru: "..." }
        const updated: Record<string, string> = {};
        for (const [key, value] of Object.entries(obj)) {
            updated[key] = typeof value === 'string'
                ? sanitizeExplanationLetter(value, newLetter)
                : String(value);
        }
        return updated;
    }
    return explanation;
}

/**
 * PR-D4-019: Fisher-Yates shuffle для опций вопроса.
 * Перемешивает options, обновляет correct_option_index,
 * чтобы правильный ответ равномерно распределялся по A/B/C/D.
 *
 * PR-D4-021: Также синхронизирует букву правильного ответа
 * в explanation (статическое заключение).
 */
export function shuffleQuestionOptions<T extends { options: string[]; correct_option_index: number }>(question: T): T {
    const opts = [...question.options];
    const correctValue = opts[question.correct_option_index];

    // Fisher-Yates shuffle
    for (let i = opts.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [opts[i], opts[j]] = [opts[j]!, opts[i]!];
    }

    const newCorrectIndex = opts.indexOf(correctValue);
    const index = newCorrectIndex >= 0 ? newCorrectIndex : 0;
    const newLetter = OPTION_LETTERS[index]!;

    const result: T = {
        ...question,
        options: opts,
        correct_option_index: index,
    };

/**
 * PR-D4-021: Sync explanation letter to match shuffled position.
 * PR-D4-032: Also sync Math 3.0 `explanations` ({ en, ru }) when present.
 */
    const qAny = question as unknown as Record<string, unknown>;
    const explanation = qAny.explanation;
    if (explanation !== undefined && explanation !== null) {
        (result as unknown as Record<string, unknown>).explanation =
            updateExplanationLetter(explanation, newLetter);
    }
    const explanations = qAny.explanations;
    if (explanations !== undefined && explanations !== null) {
        (result as unknown as Record<string, unknown>).explanations =
            updateExplanationLetter(explanations, newLetter);
    }

    return result;
}

export type TestStatus =
    | 'idle'
    | 'loading'
    | 'ready'
    | 'in-progress'
    | 'completed'
    | 'error';

export interface TestState {
    status: TestStatus;
    questions: Question[];
    currentIndex: number;
    answers: Map<number, number>;
    results: TestResult[];
    startTime: number | null;
    error: string | null;
    sessionId: string | null;
    mode: TestMode;
    studentId: string | null;
    moduleType: ProgressModuleType;
    sessionMode: ProgressSessionMode;
    fallbackDomain: string;
    draftRoute: { path: '/math-test' | '/test'; params: Record<string, string> } | null;
    submittedIndexes: Set<number>;
    lifecycleSessionId: string | null;
}

const MODULE_TIME_SECONDS = 35 * 60;
const SPARTAN_TIME_SECONDS = Math.floor(35 * 60 * 0.9);
const STEP_DEBOUNCE_MS = 400;

/** Flush the in-progress PostgreSQL cursor when the tab backgrounds. */
export function bindDraftLifecycle(saveDraft: () => void): () => void {
    const handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
            saveDraft();
        }
    };
    const handlePageHide = () => {
        saveDraft();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('pagehide', handlePageHide);
    };
}

function tierToDifficulty(tier: DifficultyTier): 'easy' | 'medium' | 'hard' {
    if (tier === 'T1') return 'easy';
    if (tier === 'T3') return 'hard';
    return 'medium';
}

export interface LoadTestOptions {
    mode: TestMode;
    studentId: string;
    topic?: string;
    tier?: DifficultyTier;
    count?: number;
    /** Math Section: domain (e.g. 'algebra', 'smart_practice') */
    domain?: MathDomain;
    /**
     * Difficulty from dashboard:
     * - Math: foundation | advanced | all
     * - Verbal (MadCrab): easy | medium | foundation | advanced
     */
    difficulty?: MathDifficulty | string;
    smartTraining?: boolean;
}

export function useTest() {
    const [state, setState] = useState<TestState>({
        status: 'idle',
        questions: [],
        currentIndex: 0,
        answers: new Map(),
        results: [],
        startTime: null,
        error: null,
        sessionId: null,
        mode: 'training',
        studentId: null,
        moduleType: 'math',
        sessionMode: 'untimed',
        fallbackDomain: 'algebra',
        draftRoute: null,
        submittedIndexes: new Set(),
        lifecycleSessionId: null,
    });

    const questionStartTimeRef = useRef<number>(Date.now());
    const persistedRef = useRef(false);
    const stateRef = useRef(state);
    stateRef.current = state;
    const stepTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const elapsedSeconds = (snapshot: TestState): number =>
        snapshot.startTime != null ? Math.max(0, Math.floor((Date.now() - snapshot.startTime) / 1000)) : 0;

    const usesLifecycle = (snapshot: TestState): boolean =>
        Boolean(snapshot.lifecycleSessionId) &&
        (snapshot.moduleType === 'math' || snapshot.moduleType === 'verbal');

    const flushSessionStep = useCallback(async (snapshot = stateRef.current) => {
        if (stepTimerRef.current) {
            clearTimeout(stepTimerRef.current);
            stepTimerRef.current = null;
        }
        if (snapshot.status !== 'in-progress' || !usesLifecycle(snapshot) || !snapshot.lifecycleSessionId) {
            return false;
        }
        const question = snapshot.questions[snapshot.currentIndex];
        const selected = snapshot.answers.get(snapshot.currentIndex);
        try {
            await api.saveSessionStep(snapshot.lifecycleSessionId, {
                currentIndex: snapshot.currentIndex,
                timeElapsedSeconds: elapsedSeconds(snapshot),
                questionId: question?.id ?? null,
                selectedOption: selected == null ? null : optionLetterFromIndex(selected),
                isCorrect:
                    selected == null || !question
                        ? null
                        : selected === question.correct_option_index,
                answerSnapshot: question ?? null,
                domain: question
                    ? questionDomain(question, snapshot.fallbackDomain)
                    : snapshot.fallbackDomain,
                skill: question?.skill_id ?? null,
                difficulty: question?.difficulty_level || question?.difficulty || null,
                timeSpentSeconds: Math.max(
                    0,
                    Math.round((Date.now() - questionStartTimeRef.current) / 1000),
                ),
            });
            return true;
        } catch {
            return false;
        }
    }, []);

    const scheduleSessionStep = useCallback(() => {
        if (stepTimerRef.current) clearTimeout(stepTimerRef.current);
        stepTimerRef.current = setTimeout(() => {
            void flushSessionStep();
        }, STEP_DEBOUNCE_MS);
    }, [flushSessionStep]);

    const restoreServerSession = useCallback((session: ActiveProgressSession, studentId: string) => {
        if (!session.questions.length) return false;
        const currentIndex = Math.min(
            Math.max(0, session.currentIndex),
            session.questions.length - 1,
        );
        const answers = answersMapFromActiveSession(session);
        questionStartTimeRef.current = Date.now();
        persistedRef.current = false;
        setState({
            status: 'in-progress',
            questions: session.questions,
            currentIndex,
            answers,
            results: [],
            startTime: Date.now() - Math.max(0, session.timeElapsedSeconds) * 1000,
            error: null,
            sessionId: session.sessionId,
            mode: session.mode === 'timed' ? 'full_practice' : 'training',
            studentId,
            moduleType: session.moduleType === 'verbal' ? 'verbal' : 'math',
            sessionMode:
                session.mode === 'timed' || session.mode === 'untimed' || session.mode === 'weak_topic_training'
                    ? session.mode
                    : 'untimed',
            fallbackDomain: session.domain || 'algebra',
            draftRoute: {
                path: session.moduleType === 'verbal' ? '/test' : '/math-test',
                params: session.moduleType === 'verbal'
                    ? { topic: `verbal/${session.domain || 'craft_structure'}` }
                    : { domain: session.domain || 'algebra' },
            },
            submittedIndexes: new Set(answers.keys()),
            lifecycleSessionId: session.sessionId,
        });
        return true;
    }, []);

    useEffect(() => {
        if (state.status !== 'in-progress') return undefined;
        return bindDraftLifecycle(() => {
            void flushSessionStep();
        });
    }, [state.status, flushSessionStep]);

    const persistCompletedSession = useCallback(async (snapshot: TestState) => {
        if (persistedRef.current || !snapshot.studentId || snapshot.questions.length === 0) return;
        persistedRef.current = true;
        const payload = buildCompletedSessionPayload({
            userId: snapshot.studentId,
            moduleType: snapshot.moduleType,
            mode: snapshot.sessionMode,
            questions: snapshot.questions,
            answersByQuestionIndex: snapshot.answers,
            results: snapshot.results,
            fallbackDomain: snapshot.fallbackDomain,
            startTime: snapshot.startTime,
        });
        try {
            if (snapshot.lifecycleSessionId) {
                await api.completeProgressSession(snapshot.lifecycleSessionId, {
                    score: payload.score ?? 0,
                    totalQuestions: payload.maxScore ?? snapshot.questions.length,
                    finalAnswers: payload.answers,
                    durationSeconds: payload.durationSeconds,
                });
            } else {
                await api.saveProgressSession(payload);
            }
        } catch {
            persistedRef.current = false;
        }
    }, []);

    const loadTest = useCallback(async (options: LoadTestOptions) => {
        const {
            mode,
            studentId,
            topic = 'geometry/circle_chords',
            tier = 'T2',
            count = 10,
            domain,
            difficulty = 'all',
            smartTraining = false,
        } = options;
        persistedRef.current = false;
        const verbalSkill = parseVerbalSkillKey(topic);
        const moduleType: ProgressModuleType = domain
            ? 'math'
            : verbalSkill || topic.startsWith('verbal/')
              ? 'verbal'
              : mode === 'full_practice'
                ? 'full_practice'
                : 'math';
        const sessionMode: ProgressSessionMode = smartTraining
            ? 'weak_topic_training'
            : mode === 'training'
              ? 'untimed'
              : 'timed';
        const fallbackDomain = domain && domain !== 'smart_practice'
            ? domain
            : verbalSkill || 'algebra';
        const routeParams: Record<string, string> = {
            mode,
            studentId,
            difficulty: String(difficulty),
        };
        if (domain) routeParams.domain = domain;
        else {
            routeParams.topic = topic;
            routeParams.tier = tier;
        }
        if (smartTraining) routeParams.smart = '1';

        setState((prev) => ({
            ...prev,
            status: 'loading',
            error: null,
            mode,
            studentId,
            moduleType,
            sessionMode,
            fallbackDomain,
            draftRoute: {
                path: domain ? '/math-test' : '/test',
                params: routeParams,
            },
            lifecycleSessionId: null,
        }));

        try {
            // Math Section: domain-based loading → GET /api/math/questions
            if (domain) {
                const mathDiff = (difficulty as MathDifficulty) || 'all';
                const response = await api.getMathQuestions(domain, mathDiff, studentId, count);
                const questions = response.questions as Question[];
                // Pool-first / seed-offline safe: never fall through to live generation.
                // Empty published pool must surface as POOL_EMPTY, not a generate HTTP 500.
                if (questions.length === 0) {
                    throw new ApiError(
                        `No published questions for ${domain}/${mathDiff}. The Math Seed generator was not used because the PostgreSQL pool is empty.`,
                        'POOL_EMPTY',
                        503,
                    );
                }

                setState((prev) => ({
                    ...prev,
                    status: 'ready',
                    questions: (questions as Question[]).map((q) =>
                        shuffleQuestionOptions({
                            ...q,
                            domain: q.domain || q.topic_id || domain,
                        }),
                    ),
                    sessionId: null,
                }));
                return;
            }

            if (mode === 'training') {
                // MadCrab_V Verbal: POST /api/questions/fast-batch (port 8787)
                const verbalSkill = parseVerbalSkillKey(topic);
                if (verbalSkill || topic.startsWith('verbal/') || topic.startsWith('rw:')) {
                    const skill =
                        verbalSkill ??
                        parseVerbalSkillKey(topic.replace(/^verbal\//, '')) ??
                        'craft_structure';
                    const verbalDiff = mapUiDifficultyToVerbal(
                        typeof difficulty === 'string' && difficulty !== 'all'
                            ? difficulty
                            : tierToDifficulty(tier),
                    );
                    const response = await api.getVerbalQuestionsFastBatch(
                        skill,
                        verbalDiff,
                        count,
                    );
                    setState((prev) => ({
                        ...prev,
                        status: 'ready',
                        questions: (response.questions as Question[]).map((q) =>
                            shuffleQuestionOptions({ ...q, domain: q.domain || skill }),
                        ),
                        sessionId: null,
                    }));
                    return;
                }

                const topicDifficulty = tierToDifficulty(tier);
                const response = await api.getQuestions(
                    topic,
                    topicDifficulty,
                    studentId,
                    count,
                );
                const questions =
                    response.questions.length > 0
                        ? (response.questions as Question[])
                        : (
                              await api.generateQuestions({
                                  topic,
                                  difficulty: topicDifficulty,
                                  count,
                              })
                          ).questions;

                setState((prev) => ({
                    ...prev,
                    status: 'ready',
                    questions: (questions as Question[]).map(shuffleQuestionOptions),
                    sessionId: null,
                }));
                return;
            }

            const moduleSize = 22;
            const sessionResponse = await api.startSession({
                student_id: studentId,
                mode,
                topic_id: topic,
                tier,
                module_size: moduleSize,
            });

            setState((prev) => ({
                ...prev,
                status: 'ready',
                questions: (sessionResponse.module_1.questions as Question[]).map(shuffleQuestionOptions),
                sessionId: sessionResponse.session.session_id,
            }));
        } catch (err) {
            const message = err instanceof ApiError ? err.message : 'Failed to load questions';
            setState((prev) => ({ ...prev, status: 'error', error: message }));
        }
    }, []);

    const startTest = useCallback(async () => {
        const snapshot = stateRef.current;
        if (snapshot.status !== 'ready' || snapshot.questions.length === 0) return;
        const moduleType = snapshot.moduleType === 'full_practice' ? 'math' : snapshot.moduleType;
        let lifecycleSessionId = snapshot.lifecycleSessionId;
        if (moduleType === 'math' || moduleType === 'verbal') {
            try {
                const created = await api.startProgressSession({
                    moduleType,
                    mode: snapshot.sessionMode,
                    domain: snapshot.fallbackDomain,
                    questionIds: snapshot.questions.map((question) => question.id),
                    questions: snapshot.questions,
                });
                lifecycleSessionId = created.sessionId;
            } catch (err) {
                const message = err instanceof ApiError ? err.message : 'Failed to start session';
                setState((prev) => ({ ...prev, status: 'error', error: message }));
                return;
            }
        }
        questionStartTimeRef.current = Date.now();
        setState((prev) => {
            const next = {
                ...prev,
                status: 'in-progress' as const,
                currentIndex: 0,
                answers: new Map<number, number>(),
                results: [],
                startTime: Date.now(),
                submittedIndexes: new Set<number>(),
                lifecycleSessionId,
                sessionId: prev.sessionId ?? lifecycleSessionId,
            };
            stateRef.current = next;
            return next;
        });
    }, []);

    const selectOption = useCallback((optionIndex: number) => {
        setState((prev) => {
            const newAnswers = new Map(prev.answers);
            newAnswers.set(prev.currentIndex, optionIndex);
            const next = { ...prev, answers: newAnswers };
            stateRef.current = next;
            return next;
        });
        scheduleSessionStep();
    }, [scheduleSessionStep]);

    const markSubmitted = useCallback(() => {
        setState((prev) => {
            const submittedIndexes = new Set(prev.submittedIndexes);
            submittedIndexes.add(prev.currentIndex);
            const next = { ...prev, submittedIndexes };
            stateRef.current = next;
            return next;
        });
        scheduleSessionStep();
    }, [scheduleSessionStep]);

    const submitAnswer = useCallback(() => {
        setState((prev) => {
            const selectedOption = prev.answers.get(prev.currentIndex);
            const currentQuestion = prev.questions[prev.currentIndex];
            if (selectedOption === undefined || !currentQuestion) return prev;

            const timeSpent = Math.round((Date.now() - questionStartTimeRef.current) / 1000);
            const result: TestResult = {
                questionId: currentQuestion.id,
                selectedOption,
                correctOption: currentQuestion.correct_option_index,
                isCorrect: selectedOption === currentQuestion.correct_option_index,
                timeSpentSeconds: timeSpent,
            };

            if (prev.sessionId && prev.mode !== 'training') {
                void api.recordSessionAnswer(
                    prev.sessionId,
                    currentQuestion.id,
                    selectedOption,
                    timeSpent,
                );
            }

            const newResults = [...prev.results, result];
            const nextIndex = prev.currentIndex + 1;

            if (nextIndex >= prev.questions.length) {
                const completed: TestState = { ...prev, results: newResults, status: 'completed' };
                queueMicrotask(() => {
                    void persistCompletedSession(completed);
                });
                return completed;
            }

            questionStartTimeRef.current = Date.now();
            const next = { ...prev, results: newResults, currentIndex: nextIndex };
            stateRef.current = next;
            return next;
        });
        scheduleSessionStep();
    }, [persistCompletedSession, scheduleSessionStep]);

    const goToQuestion = useCallback((index: number) => {
        setState((prev) => {
            if (index < 0 || index >= prev.questions.length) return prev;
            const next = { ...prev, currentIndex: index };
            stateRef.current = next;
            return next;
        });
        scheduleSessionStep();
    }, [scheduleSessionStep]);

    const finishTest = useCallback(async () => {
        await flushSessionStep();
        const selectedOption = state.answers.get(state.currentIndex);
        const currentQuestion = state.questions[state.currentIndex];
        const hasCurrentResult = currentQuestion
            ? state.results.some((result) => result.questionId === currentQuestion.id)
            : false;
        const currentResult =
            selectedOption !== undefined && currentQuestion && !hasCurrentResult
                ? {
                      questionId: currentQuestion.id,
                      selectedOption,
                      correctOption: currentQuestion.correct_option_index,
                      isCorrect: selectedOption === currentQuestion.correct_option_index,
                      timeSpentSeconds: Math.round(
                          (Date.now() - questionStartTimeRef.current) / 1000,
                      ),
                  }
                : null;
        const results = currentResult ? [...state.results, currentResult] : state.results;
        const completed: TestState = { ...state, results, status: 'completed' };

        if (state.sessionId && state.mode !== 'training') {
            try {
                const answers = results.map((r) => ({
                    question_id: r.questionId,
                    selected_option_index: r.selectedOption ?? -1,
                    time_spent_seconds: r.timeSpentSeconds,
                }));
                await api.submitModule1(state.sessionId, answers);
            } catch {
                // Results still shown client-side
            }
        }
        if (currentResult && state.sessionId && state.mode !== 'training') {
            void api.recordSessionAnswer(
                state.sessionId,
                currentResult.questionId,
                currentResult.selectedOption,
                currentResult.timeSpentSeconds,
            );
        }
        await persistCompletedSession(completed);
        setState((prev) => ({ ...prev, results, status: 'completed' }));
    }, [state, persistCompletedSession, flushSessionStep]);

    const reset = useCallback(() => {
        persistedRef.current = false;
        setState({
            status: 'idle',
            questions: [],
            currentIndex: 0,
            answers: new Map(),
            results: [],
            startTime: null,
            error: null,
            sessionId: null,
            mode: 'training',
            studentId: null,
            moduleType: 'math',
            sessionMode: 'untimed',
            fallbackDomain: 'algebra',
            draftRoute: null,
            submittedIndexes: new Set(),
            lifecycleSessionId: null,
        });
    }, []);

    const currentQuestion =
        state.questions.length > 0 ? state.questions[state.currentIndex] : null;

    const score = state.results.filter((r) => r.isCorrect).length;
    const totalAnswered = state.results.length;

    const totalTimeSeconds =
        state.mode === 'spartan'
            ? SPARTAN_TIME_SECONDS
            : state.mode === 'training'
              ? 0
              : MODULE_TIME_SECONDS;

    const timerEnabled = state.mode !== 'training';

    return {
        ...state,
        currentQuestion,
        score,
        totalAnswered,
        totalTimeSeconds,
        timerEnabled,
        loadTest,
        startTest,
        selectOption,
        submitAnswer,
        goToQuestion,
        finishTest,
        markSubmitted,
        saveDraft: flushSessionStep,
        flushSessionStep,
        restoreServerSession,
        reset,
    };
}
